"use client";

import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/lib/i18n';

/**
 * 自定义 i18n Hook
 * 提供类型安全的多语言功能
 * 
 * 注意：此 hook 需要 react-i18next 和相关配置，请确保已安装：
 * - npm install react-i18next i18next i18next-browser-languagedetector
 * - 配置 i18n 初始化（参考 nouso-web 的配置）
 */
export function useI18n() {
	const { t, i18n } = useTranslation();

	// 获取当前语言
	const currentLanguage = i18n.language as SupportedLanguage;

	// 切换语言
	const changeLanguage = useCallback(
		(language: SupportedLanguage) => {
			// 立即保存到 localStorage
			if (typeof window !== 'undefined') {
				localStorage.setItem('i18nextLng', language);
			}

			// 切换语言
			return i18n.changeLanguage(language).then(() => {
				return language;
			});
		},
		[i18n]
	);

	// 检查是否为指定语言
	const isLanguage = useCallback(
		(language: SupportedLanguage) => {
			return currentLanguage === language;
		},
		[currentLanguage]
	);

	// 检查是否为中文
	const isChinese = useCallback(() => {
		return isLanguage('zh');
	}, [isLanguage]);

	// 检查是否为英文
	const isEnglish = useCallback(() => {
		return isLanguage('en');
	}, [isLanguage]);

	// 获取支持的语言列表
	const supportedLanguages = SUPPORTED_LANGUAGES;

	// 类型安全的翻译函数
	const translate = useCallback(
		(key: string, options?: any): string => {
			return t(key, options) as string;
		},
		[t]
	);

	// 翻译并格式化
	const translateWithFormat = useCallback(
		(key: string, values: Record<string, any> = {}): string => {
			return t(key, values) as string;
		},
		[t]
	);

	// 翻译复数形式
	const translatePlural = useCallback(
		(key: string, count: number, options?: any): string => {
			return t(key, { count, ...options }) as string;
		},
		[t]
	);

	// 翻译插值
	const translateInterpolation = useCallback(
		(key: string, interpolation: Record<string, any>): string => {
			return t(key, interpolation) as string;
		},
		[t]
	);

	return {
		// 翻译函数
		t: translate,
		translate: translateWithFormat,
		translatePlural,
		translateInterpolation,

		// 语言相关
		currentLanguage,
		changeLanguage,
		isLanguage,
		isChinese,
		isEnglish,
		supportedLanguages,

		// i18n 实例
		i18n,
	};
}

/**
 * 命名空间专用的 i18n Hook
 * @param namespace 命名空间
 */
export function useI18nNamespace(namespace: string) {
	const { t, i18n } = useTranslation(namespace);
	const { currentLanguage, changeLanguage, isLanguage, isChinese, isEnglish } = useI18n();

	// 命名空间翻译函数
	const translate = useCallback(
		(key: string, options?: any): string => {
			return t(key, options) as string;
		},
		[t]
	);

	return {
		t: translate,
		currentLanguage,
		changeLanguage,
		isLanguage,
		isChinese,
		isEnglish,
		i18n,
	};
}

/**
 * 通用文案 Hook
 */
export function useCommonI18n() {
	return useI18nNamespace('common');
}

/**
 * 认证相关文案 Hook
 */
export function useAuthI18n() {
	return useI18nNamespace('auth');
}

/**
 * 文献相关文案 Hook
 */
export function usePapersI18n() {
	return useI18nNamespace('papers');
}

/**
 * 工作流相关文案 Hook
 */
export function useWorkflowI18n() {
	return useI18nNamespace('workflow');
}

/** AI 写作相关文案 Hook */
export function useWriterI18n() {
	return useI18nNamespace('writer');
}

/**
 * UI 组件文案 Hook
 */
export function useUII18n() {
	return useI18nNamespace('ui');
}

/**
 * Dashboard 相关文案 Hook
 */
export function useDashboardI18n() {
	return useI18nNamespace('dashboard');
}

/**
 * Sidebar 相关文案 Hook
 */
export function useSidebarI18n() {
	return useI18nNamespace('sidebar');
}

/**
 * Subjects 相关文案 Hook
 */
export function useSubjectsI18n() {
	return useI18nNamespace('subjects');
}

/**
 * Activities 相关文案 Hook
 */
export function useActivitiesI18n() {
	return useI18nNamespace('activities');
}

/**
 * Site 相关文案 Hook
 */
export function useSiteI18n() {
	return useI18nNamespace('site');
}

/**
 * Meta 相关文案 Hook
 */
export function useMetaI18n() {
	return useI18nNamespace('meta');
}

/**
 * Assistants 相关文案 Hook
 */
export function useAssistantsI18n() {
	return useI18nNamespace('assistants');
}

/**
 * Balances 相关文案 Hook
 */
export function useBalancesI18n() {
	return useI18nNamespace('balances');
}

/**
 * Payment 相关文案 Hook
 */
export function usePaymentI18n() {
	return useI18nNamespace('payment');
}

/**
 * Notes 相关文案 Hook
 */
export function useNotesI18n() {
	return useI18nNamespace('notes');
}

/**
 * Chats 相关文案 Hook
 */
export function useChatsI18n() {
	return useI18nNamespace('chats');
}

/**
 * Errors 相关文案 Hook
 */
export function useErrorsI18n() {
	return useI18nNamespace('errors');
}

/**
 * Guides 相关文案 Hook
 */
export function useGuidesI18n() {
	return useI18nNamespace('guides');
}

/**
 * Mindmap 相关文案 Hook
 */
export function useMindmapI18n() {
	return useI18nNamespace('mindmap');
}

/**
 * Rest 相关文案 Hook
 */
export function useRestI18n() {
	return useI18nNamespace('rest');
}

/**
 * Upload 相关文案 Hook
 */
export function useUploadI18n() {
	return useI18nNamespace('upload');
}
